import * as Sentry from '@sentry/react'

import Env from 'config/Env'

import {
  SafeDetails,
  summariseError,
  summarisePayload,
  summariseResponse,
  toSafeMessageCode,
} from './scrubSentryData'

// Every helper below reduces its input to safe details before it reaches Sentry (US 16919).
// Callers still pass whole bodies and responses; keeping the signatures means no call site
// has to remember to scrub, and a new call site cannot forget to.

const logApiBody = (title: string, details: SafeDetails, logType: string, userId?: string) => {
  if (!Env.isLocal()) {
    Sentry.captureMessage(`${title} - ${logType}`, {
      level: 'info',
      extra: { ...details, logType },
      tags: { userId },
    })
  }
}

/**
 * Logs that a request was made. Only the payload's shape and top-level record ids are sent.
 * @param title log label, usually a `log.*` translation.
 * @param requestData request body; never sent as-is.
 * @param userId id of the signed-in user.
 */
export const logApiRequest = (title: string, requestData: unknown, userId?: string) => {
  logApiBody(title, summarisePayload(requestData), 'API Request', userId)
}

/**
 * Logs a request's outcome — endpoint, HTTP status and the envelope's success flag and code.
 * @param title log label, usually a `log.*` translation.
 * @param responseData axios response; never sent as-is, as it holds the token and the body.
 * @param userId id of the signed-in user.
 */
export const logApiResponse = (title: string, responseData: unknown, userId?: string) => {
  logApiBody(title, summariseResponse(responseData), 'API Response', userId)
}

/**
 * Logs an `isSuccess: false` response. Free-text messages are replaced with a marker.
 * @param message `message` from the API envelope.
 */
export const logApiRequestFailed = (message: string) => {
  if (!Env.isLocal()) {
    Sentry.captureMessage(`API Request Failed`, {
      level: 'error',
      extra: { messageCode: toSafeMessageCode(message) ?? null },
    })
  }
}

/**
 * Logs a caught exception with its transport details and stack, without request or
 * response bodies.
 * @param error value from a `catch`.
 */
export const logApiException = (error: unknown) => {
  if (!Env.isLocal()) {
    Sentry.captureMessage(`API Exception`, { level: 'error', extra: summariseError(error) })
  }
}

/**
 * Logs an app event. `data` is reduced to record ids and a few known-safe fields.
 * @param title event label.
 * @param data context for the event; never sent as-is.
 */
export const logTransaction = (title: string, data?: unknown) => {
  if (!Env.isLocal()) {
    Sentry.captureMessage(title, { level: 'info', extra: summarisePayload(data) })
  }
}
